import React, { useState, useEffect } from 'react';
import { useAdminStore } from '../../../../store/adminStore'; 
import { api } from '../../../../services/apiServices'; 
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { Badge } from '../../../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../../../components/ui/tabs'; // Assumes you have shadcn Tabs
import { Loader2, Eye, Plus, Globe, Check, Pencil, Users } from 'lucide-react';
import { CategorySearch } from '../../../../components/Admin/Category/CategorySearch';
import PaginationControls from '../../../../components/Shared/PaginationControls'; 

const LANGUAGES = ["EN", "HI", "FR", "ES", "DE"]; 

const SeoCategoryMetaManager = () => {
  const { locale, countries, setLocale, setCountries } = useAdminStore((state) => state);
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  // Filters
  const [filterCategory, setFilterCategory] = useState(null);
  const [filterLang, setFilterLang] = useState('ALL');
  const [filterAudience, setFilterAudience] = useState('BUYER'); // Default to Buyer

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); 
  const [editingItem, setEditingItem] = useState(null); 
  
  const [formData, setFormData] = useState({
    category: null,
    language_code: 'EN',
    audience: 'BUYER',
    intro_content: '',
    pro_tip_content: ''
  });

  // --- Init ---
  useEffect(() => {
    if (countries.length === 0) {
      api.get('/admin/common/active-countries')
        .then(res => {
           setCountries(res.data || []);
           if (!locale && res.data && res.data.length > 0) setLocale(res.data[0].country_code);
        })
        .catch(err => console.error("Failed to fetch countries:", err));
    }
  }, [countries.length, setCountries, locale, setLocale]);

  // --- Fetch Data ---
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        country_code: locale,
        page: pagination.page,
        limit: pagination.limit,
        category_id: filterCategory?.id,
        language_code: filterLang !== 'ALL' ? filterLang : undefined,
        audience: filterAudience // Filter by tab
      };
      
      const res = await api.get('/admin/seo/category-meta', { params });
      setData(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total }));
    } catch (err) {
      console.error("Failed to fetch meta", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (locale) fetchData();
  }, [locale, pagination.page, filterCategory, filterLang, filterAudience]);

  // --- Handlers ---

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    setFormData({
        category: null,
        language_code: 'EN',
        audience: filterAudience, // Default to current tab
        intro_content: '',
        pro_tip_content: ''
    });
    setIsEditMode(true); 
    setIsModalOpen(true);
  };

  const handleOpenView = (item) => {
    setEditingItem(item);
    setFormData({
        category: { id: item.category_id, name: item.category_name }, 
        language_code: item.language_code,
        audience: item.audience,
        intro_content: item.intro_content || '',
        pro_tip_content: item.pro_tip_content || ''
    });
    setIsEditMode(false); 
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        // PUT URL now includes audience
        await api.put(`/admin/seo/category-meta/${editingItem.category_id}/${editingItem.language_code}/${editingItem.audience}`, {
            intro_content: formData.intro_content,
            pro_tip_content: formData.pro_tip_content
        });
      } else {
        if (!formData.category?.id) return alert("Please select a category");
        
        await api.post('/admin/seo/category-meta', {
            category_id: formData.category.id,
            language_code: formData.language_code,
            audience: formData.audience,
            intro_content: formData.intro_content,
            pro_tip_content: formData.pro_tip_content
        });
      }
      setIsModalOpen(false);
      fetchData(); 
    } catch (err) {
      alert("Error saving: " + (err.response?.data?.detail || err.message));
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="h-full flex flex-col p-6 space-y-4 bg-gray-50/50">
      
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg border shadow-sm">
        <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold text-gray-800">Category Meta</h2>
            
            {/* Country Selector */}
            <div className="flex items-center gap-2 border px-3 py-1.5 rounded-md bg-gray-50">
                <Globe className="w-4 h-4 text-gray-500" />
                <Select value={locale || ''} onValueChange={setLocale}>
                    <SelectTrigger className="w-[140px] h-8 border-none bg-transparent focus:ring-0 p-0">
                        <SelectValue placeholder="Select Country" />
                    </SelectTrigger>
                    <SelectContent>
                        {countries.map((c) => (
                            <SelectItem key={c.country_code} value={c.country_code}>{c.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2">
            <Plus className="w-4 h-4"/> Add Content
        </Button>
      </div>

      {/* Main Filters (Audience Tabs + Category) */}
      <div className="flex flex-col gap-4 bg-white p-4 rounded border shadow-sm">
         
         {/* Audience Tabs */}
         <Tabs value={filterAudience} onValueChange={setFilterAudience} className="w-full">
            <TabsList className="grid w-[400px] grid-cols-2">
                <TabsTrigger value="BUYER">Buyer (Leads)</TabsTrigger>
                <TabsTrigger value="SELLER">Seller (Partners)</TabsTrigger>
            </TabsList>
         </Tabs>

         <div className="flex gap-4 items-end">
            <div className="w-[300px]">
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Category</label>
                <CategorySearch 
                    locale={locale} 
                    onSelect={(c) => {
                        setFilterCategory(c);
                        setPagination(p => ({...p, page: 1})); 
                    }} 
                    placeholder={filterCategory ? filterCategory.name : "Filter by Category..."}
                />
                {filterCategory && (
                    <button onClick={() => {
                        setFilterCategory(null);
                        setPagination(p => ({...p, page: 1}));
                    }} className="text-xs text-red-500 mt-1 hover:underline">Clear Category</button>
                )}
            </div>
            <div className="w-[150px]">
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Language</label>
                <Select value={filterLang} onValueChange={(val) => {
                    setFilterLang(val);
                    setPagination(p => ({...p, page: 1}));
                }}>
                    <SelectTrigger><SelectValue placeholder="Language" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">All Languages</SelectItem>
                        {LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
         </div>
      </div>

      {/* Table */}
      <div className="flex-1 bg-white border rounded-md overflow-auto shadow-sm flex flex-col">
        <Table>
            <TableHeader className="bg-muted/50 sticky top-0">
                <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead>Language</TableHead>
                    <TableHead className="text-center w-[80px]">Intro</TableHead>
                    <TableHead className="text-center w-[80px]">Pro Tip</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {loading ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-20"><Loader2 className="animate-spin mx-auto w-6 h-6"/></TableCell></TableRow>
                ) : data.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-20 text-gray-400">No records found for {locale} ({filterAudience}).</TableCell></TableRow>
                ) : (
                    data.map((item) => (
                        <TableRow key={`${item.category_id}-${item.language_code}-${item.audience}`} className="hover:bg-gray-50">
                            <TableCell className="font-medium">
                                <div>{item.category_name}</div>
                                <div className="text-[10px] text-gray-400">{item.category_type}</div>
                            </TableCell>
                            <TableCell>
                                <Badge variant={item.audience === 'BUYER' ? "default" : "secondary"}>
                                    {item.audience}
                                </Badge>
                            </TableCell>
                            <TableCell><Badge variant="outline">{item.language_code}</Badge></TableCell>
                            
                            <TableCell className="text-center">
                                {item.intro_content ? <div className="flex justify-center"><Check className="w-4 h-4 text-green-600" /></div> : <span className="text-gray-300 text-xs">-</span>}
                            </TableCell>

                            <TableCell className="text-center">
                                {item.pro_tip_content ? <div className="flex justify-center"><Check className="w-4 h-4 text-green-600" /></div> : <span className="text-gray-300 text-xs">-</span>}
                            </TableCell>

                            <TableCell className="text-xs text-gray-400">
                                {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                                <Button variant="ghost" size="sm" onClick={() => handleOpenView(item)}>
                                    <Eye className="w-4 h-4 mr-1 text-gray-500" /> View
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))
                )}
            </TableBody>
        </Table>
        
        {data.length > 0 && (
            <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
                <p className="text-xs text-gray-500">
                    Showing {data.length} of {pagination.total} records
                </p>
                <PaginationControls 
                    currentPage={pagination.page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </div>
        )}
      </div>

      {/* CREATE / VIEW / EDIT MODAL */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="flex flex-row items-center justify-between">
                <DialogTitle>
                    {editingItem ? (isEditMode ? 'Edit Content' : 'View Content') : 'Add New Content'}
                </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label>Category</Label>
                        {editingItem ? (
                            <Input value={formData.category?.name} disabled className="bg-gray-100" />
                        ) : (
                            <CategorySearch 
                                locale={locale} 
                                onSelect={(cat) => setFormData(prev => ({...prev, category: cat}))}
                            />
                        )}
                    </div>
                    <div>
                        <Label>Audience</Label>
                        {editingItem ? (
                             <Input value={formData.audience} disabled className="bg-gray-100" />
                        ) : (
                            <Select 
                                value={formData.audience} 
                                onValueChange={(val) => setFormData(prev => ({...prev, audience: val}))}
                            >
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="BUYER">BUYER</SelectItem>
                                    <SelectItem value="SELLER">SELLER</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                </div>
                
                {/* Language Select */}
                {!editingItem && (
                    <div className="w-1/2">
                        <Label>Language</Label>
                        <Select 
                            value={formData.language_code} 
                            onValueChange={(val) => setFormData(prev => ({...prev, language_code: val}))}
                        >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                )}
                
                {/* Text Areas */}
                <div>
                    <Label className="mb-1 block">Intro Content</Label>
                    <Textarea 
                        rows={5}
                        className={`transition-colors ${!isEditMode ? 'bg-gray-100 text-gray-700' : 'bg-white'}`}
                        value={formData.intro_content} 
                        onChange={(e) => setFormData(prev => ({...prev, intro_content: e.target.value}))}
                        placeholder={isEditMode ? `Write ${formData.audience} intro...` : "No content."}
                        disabled={!isEditMode}
                    />
                </div>
                <div>
                    <Label className="mb-1 block">Pro Tip / Subtext</Label>
                    <Textarea 
                        rows={3}
                        className={`transition-colors ${!isEditMode ? 'bg-gray-100 text-gray-700' : 'bg-white'}`}
                        value={formData.pro_tip_content} 
                        onChange={(e) => setFormData(prev => ({...prev, pro_tip_content: e.target.value}))}
                        placeholder={isEditMode ? "Add extra tips..." : "No content."}
                        disabled={!isEditMode}
                    />
                </div>
            </div>

            <DialogFooter>
                {!isEditMode ? (
                    <Button onClick={() => setIsEditMode(true)} variant="outline" className="gap-2">
                        <Pencil className="w-4 h-4"/> Edit Content
                    </Button>
                ) : (
                    <div className="flex gap-2">
                        {editingItem && (
                            <Button onClick={() => setIsEditMode(false)} variant="ghost">Cancel</Button>
                        )}
                        <Button onClick={handleSave}>Save Changes</Button>
                    </div>
                )}
            </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeoCategoryMetaManager;